import {
    Component,
    ElementRef,
    computed,
    effect,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button, ButtonVariant, Modal, ModalSizeVariant } from '@ntv360/component-pantry';
import lottie, { AnimationItem } from 'lottie-web';
import { SAFE_END_FRAME } from '@shared/constants';

export type ConfirmationModalType =
    | 'success'
    | 'edit'
    | 'warning'
    | 'delete'
    | 'error'
    | 'delete-confirm';

@Component({
    selector: 'app-confirmation-modal',
    standalone: true,
    imports: [Modal, Button, FormsModule],
    templateUrl: './confirmation-modal.component.html',
    styleUrl: './confirmation-modal.component.scss',
})
export class ConfirmationModalComponent {
    /** ViewChild for the lottie container */
    readonly lottieContainer = viewChild<ElementRef>('lottieContainer');

    /** Whether the modal is visible */
    public isVisible = input.required<boolean>();

    /** The type of confirmation modal */
    public type = input<ConfirmationModalType>('success');

    /** The modal size */
    public size = input<ModalSizeVariant>('medium');

    /** The title of the modal */
    public title = input<string>('Modal title');

    /** The message/body text of the modal */
    public message = input<string>('Modal message');

    /** Text for the primary action button */
    public confirmText = input<string>('Confirm');

    /** Text for the cancel/back button */
    public cancelText = input<string>('Cancel');

    /** Whether to show the cancel button. Defaults to `false` */
    public showCancelButton = input<boolean>(false);

    /** Variant for the cancel button. Defaults to `danger` */
    public cancelVariant = input<ButtonVariant>('danger');

    /** Whether to loop the lottie animation. Defaults to `true` */
    public loopAnimation = input<boolean>(true);

    /** Whether async operation is underway, eg. deleting content show spinner */
    public isLoading = input<boolean>(false);

    /** Emits when the modal is closed */
    public modalClose = output<void>();

    /** Emits when the confirm button is clicked */
    public confirm = output<void>();

    /** Emits when the cancel button is clicked */
    public cancel = output<void>();

    /** Emits when any action button is clicked, with the type of action */
    public action = output<string>();

    /** The height of the modal */
    public height = input<string>('auto');

    /**
     * Optional disclaimer/note rendered below the message.
     * Use to surface side-effects or scope clarifications (e.g. what is/isn't affected).
     */
    public disclaimer = input<string>('');

    /** Text the user must type to enable the confirm action. Used by `delete-confirm` type. */
    public requiredText = input<string>('');

    /** Placeholder for the confirmation input. Used by `delete-confirm` type. */
    public confirmInputPlaceholder = input<string>('Type here to confirm');

    /** Internal value typed by the user in the confirmation input. */
    protected typedValue = signal<string>('');

    /**
     * Whether the confirm button should be disabled.
     *
     * @remarks
     * For `delete-confirm`, requires the typed value to match `requiredText` exactly
     * (case-sensitive, trimmed). Other types are never disabled by this modal.
     */
    protected isConfirmDisabled = computed<boolean>(() => {
        if (this.type() !== 'delete-confirm') return false;
        const required = this.requiredText().trim();
        return required.length === 0 || this.typedValue().trim() !== required;
    });

    protected animationPath = '';

    /** Reference to this modal's own Lottie instance, so only it gets destroyed on reload */
    private animationInstance: AnimationItem | null = null;

    /** Guards against reloading the animation more than once per open, even if the
     * container signal fires again for unrelated reasons while still visible. */
    private hasLoadedAnimation: boolean = false;

    constructor() {
        effect(() => {
            const isVisible = this.isVisible();
            const container = this.lottieContainer();

            if (!isVisible) {
                this.typedValue.set('');
                this.hasLoadedAnimation = false;
                return;
            }

            if (container && !this.hasLoadedAnimation) {
                this.hasLoadedAnimation = true;
                this.loadAnimation(container);
            }
        });
    }

    /**
     * Loads the appropriate Lottie animation based on the modal type.
     * Called at most once per modal-open lifecycle (see `hasLoadedAnimation`).
     *
     * @param container - The lottie container element ref.
     */
    private loadAnimation(container: ElementRef): void {
        const type = this.type();
        let animationName = '';

        switch (type) {
            case 'success':
                animationName = 'success.json';
                break;
            case 'error':
                animationName = 'error-animation.json';
                break;
            case 'delete':
                animationName = 'deleting-animation.json';
                break;
            case 'warning':
            case 'delete-confirm':
                animationName = 'warning.json';
                break;
            default:
                animationName = 'loading-animation.json';
        }

        this.animationPath = `assets/lottie-animations/${animationName}`;

        if (this.animationInstance) {
            this.animationInstance.destroy();
            this.animationInstance = null;
        }

        const loop = this.loopAnimation();
        const animation = lottie.loadAnimation({
            container: container.nativeElement,
            renderer: 'svg',
            loop,
            // When not looping, hold off autoplay: we explicitly play only up to the
            // safe end frame below, instead of letting it run into a fade-out tail.
            autoplay: loop,
            path: this.animationPath,
            // Lottie's SVG renderer defaults to hiding the container (visibility: hidden)
            // whenever the current frame is fully transparent. A frozen last frame with
            // any fading decorative layer trips this, making the whole icon vanish.
            rendererSettings: { hideOnTransparent: false },
        });
        this.animationInstance = animation;

        if (!loop) {
            animation.addEventListener('DOMLoaded', () => {
                const endFrame = SAFE_END_FRAME[animationName] ?? animation.totalFrames - 1;
                animation.playSegments([0, endFrame], true);
            });
        }
    }

    /**
     * Handles the close action
     */
    protected onClose(): void {
        this.modalClose.emit();
    }

    /**
     * Handles the confirm action
     */
    protected onConfirm(): void {
        if (this.isConfirmDisabled()) return;
        this.action.emit(this.type());
        this.confirm.emit();
    }

    /**
     * Updates the typed value used to gate the confirm action.
     *
     * @param value - The current value of the confirmation input.
     */
    protected onTypedValueChange(value: string): void {
        this.typedValue.set(value);
    }

    /**
     * Handles the cancel action
     */
    protected onCancel(): void {
        this.action.emit('cancel');
        this.cancel.emit();
    }
}